import { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional label shown in the fallback so the user knows where it happened. */
  area?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Page-level error boundary. Catches render-time exceptions in any descendant
 * (e.g. Rules of Hooks violations, undefined .map calls, unhandled exceptions
 * inside JSX expressions) and renders a recoverable fallback instead of a
 * blank white page.
 *
 * Wrap the main content area of AppLayout, not the whole app, so the sidebar
 * and header stay usable when a single page blows up.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary] caught render error:", error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <AlertTriangle className="w-12 h-12 text-destructive mb-4" />
        <h2 className="text-xl font-semibold mb-2">Something went wrong</h2>
        <p className="text-sm text-muted-foreground mb-1">
          {this.props.area ? `An error occurred in ${this.props.area}.` : "This page hit an unexpected error."}
        </p>
        <pre className="text-xs text-muted-foreground/80 max-w-2xl whitespace-pre-wrap break-words mt-2 mb-6 px-4">
          {this.state.error.message}
        </pre>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={this.handleReset}>
            Try again
          </Button>
          <Button size="sm" onClick={this.handleReload} className="gap-1.5">
            <RotateCcw className="w-3.5 h-3.5" /> Reload page
          </Button>
        </div>
      </div>
    );
  }
}
